<script lang="ts">
  /**
   * Minimal in-app modal. No native confirm/alert/prompt anywhere — P7.
   */
  import { onMount } from "svelte";

  type Props = {
    title: string;
    onClose: () => void;
    children?: any;
  };
  const { title, onClose, children }: Props = $props();

  // A92: focus trap + autofocus + restore del foco al cerrar.
  let modalEl: HTMLDivElement | null = $state(null);
  let previouslyFocused: HTMLElement | null = null;

  function focusableEls(): HTMLElement[] {
    if (!modalEl) return [];
    return Array.from(
      modalEl.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    // Trap: el foco no puede escaparse a la página de fondo.
    const els = focusableEls();
    if (els.length === 0) return;
    const first = els[0];
    const last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  onMount(() => {
    previouslyFocused = document.activeElement as HTMLElement | null;
    window.addEventListener("keydown", onKey);
    // Autofocus: primer campo enfocable (ej. el input del PIN) o, si no hay
    // ninguno, el modal mismo — así el teclado virtual aparece solo.
    const els = focusableEls();
    (els[0] ?? modalEl)?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  });
</script>

<div class="backdrop" role="dialog" aria-modal="true" aria-label={title} onclick={onClose}>
  <div class="modal" role="document" tabindex="-1" bind:this={modalEl} onclick={(e) => e.stopPropagation()}>
    <h2>{title}</h2>
    <div class="body">
      {@render children?.()}
    </div>
  </div>
</div>

<style>
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex; align-items: center; justify-content: center;
    /* Un diálogo (incl. ConfirmModal) debe quedar por ENCIMA de cualquier
       otro overlay de la app — p.ej. el modal del día del planificador
       (z-index 31) — y por DEBAJO de los toasts (100). Antes era 10, por eso
       la confirmación de borrar turno aparecía detrás y "no hacía nada". */
    z-index: 90;
    padding: 1rem;
  }
  .modal {
    background: #1d2230;
    color: #e7e9ee;
    border-radius: 16px;
    padding: 1.5rem;
    max-width: 480px; width: 100%;
    max-height: calc(100dvh - 2rem);
    overflow-y: auto;
    box-shadow: 0 16px 64px rgba(0, 0, 0, 0.5);
  }
  h2 { margin: 0 0 1rem; font-size: 1.25rem; }
</style>
